import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/DatePicker'

type AccLine = { account_id: number; code: string; name: string; balance: string }

type PL = {
  from: string | null
  to: string | null
  revenue: AccLine[]
  total_revenue: string
  cost_of_sales: AccLine[]
  total_cost_of_sales: string
  gross_profit: string
  expenses: AccLine[]
  total_expenses: string
  net_profit: string
}

function yearStart() {
  const d = new Date()
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10)
}
function today() { return new Date().toISOString().slice(0, 10) }

export default function ReportProfitLoss() {
  const [from, setFrom] = useState(yearStart())
  const [to, setTo] = useState(today())

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'pl', from, to],
    queryFn: async () => {
      return (await api.get<PL>('/finance/reports/profit-loss/', { params: { from, to } })).data
    },
  })

  return (
    <AdminLayout>
      <h2 className="text-2xl font-bold text-gray-800 mb-1">Profit & Loss</h2>
      <p className="text-sm text-gray-500 mb-6">
        Revenue minus Cost of Sales = Gross Profit. Minus Operating Expenses = Net Profit.
      </p>

      <div className="flex items-end space-x-3 mb-4 bg-white p-3 rounded-lg shadow">
        <Field>
          <FieldLabel htmlFor="pl-from">From</FieldLabel>
          <DatePicker id="pl-from" value={from} onChange={(v) => setFrom(v)} />
        </Field>
        <Field>
          <FieldLabel htmlFor="pl-to">To</FieldLabel>
          <DatePicker id="pl-to" value={to} onChange={(v) => setTo(v)} />
        </Field>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : !data ? null : (
        <div className="bg-white rounded-lg shadow p-6 max-w-3xl">
          <Section title="Revenue" rows={data.revenue} total={data.total_revenue} />
          <Section title="Cost of Sales" rows={data.cost_of_sales} total={data.total_cost_of_sales} />
          <SubtotalRow label="Gross Profit" value={data.gross_profit} highlight />
          <Section title="Operating Expenses" rows={data.expenses} total={data.total_expenses} />
          <SubtotalRow label="Net Profit" value={data.net_profit} highlight bold />
        </div>
      )}
    </AdminLayout>
  )
}

function Section({ title, rows, total }: { title: string; rows: AccLine[]; total: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 border-b border-gray-200 pb-1">
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 italic py-1 pl-3">— no activity —</p>
      ) : (
        <table className="min-w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.account_id}>
                <td className="pl-3 py-1 font-mono text-xs text-gray-500 w-24">{r.code}</td>
                <td className="py-1">{r.name}</td>
                <td className="py-1 text-right font-mono">{Number(r.balance).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-gray-200">
            <tr className="font-semibold">
              <td></td>
              <td className="pt-1 text-gray-700">Total {title}</td>
              <td className="pt-1 text-right font-mono">{Number(total).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  )
}

function SubtotalRow({ label, value, highlight, bold }: { label: string; value: string; highlight?: boolean; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-2 my-2 px-3 rounded ${
      highlight ? (Number(value) >= 0 ? 'bg-green-50' : 'bg-red-50') : ''
    } ${bold ? 'font-bold' : 'font-semibold'}`}>
      <span className={highlight ? (Number(value) >= 0 ? 'text-green-800' : 'text-red-800') : 'text-gray-700'}>
        {label}
      </span>
      <span className={`font-mono ${highlight ? (Number(value) >= 0 ? 'text-green-800' : 'text-red-800') : 'text-gray-900'}`}>
        {Number(value).toFixed(2)}
      </span>
    </div>
  )
}

